/**
 * mapperSerializer  (SM-20 / DA-2293)
 *
 * Client-side port of the BE serializer (bidgely-quill survey-mapper-serializer.ts) used
 * for LIVE PREVIEW as the admin edits in the guided builder. The BE remains authoritative
 * on save (SM-11); this must produce byte-identical output to it so the preview matches.
 * Grounded in docs/survey-mapper-grammar.md.
 */
import {
  AnswerChoice,
  ApMapperClause,
  ApplianceQuestionLink,
  HomeProfileQuestionLink,
  HpMapperClause,
  MapperRole,
} from 'types/apis/surveyInterfaces'

const NULL = 'null'

/** C-operator value: `a-b` (upper-exclusive) / `a-` / bare `n` (equality). */
function serializeRange(c: { rangeStart?: string; rangeEnd?: string; value?: string }): string {
  const start = c.rangeStart ?? ''
  const end = c.rangeEnd ?? ''
  if (start !== '' || end !== '') return `${start}-${end}`
  return c.value ?? ''
}

export function serializeStateMapper(
  links: { homeProfileLinks?: HomeProfileQuestionLink[]; applianceLinks?: ApplianceQuestionLink[] },
): string {
  const parts: string[] = []
  for (const l of links.homeProfileLinks ?? []) parts.push(`HP-${l.attributePath}`)
  for (const l of links.applianceLinks ?? []) {
    let token = `AP:${l.applianceId}-${l.operator}`
    if (l.operator === 'A' && l.attributeKey) token += `:${l.attributeKey}`
    parts.push(token)
  }
  return parts.join(',')
}

/**
 * Roll a question's choices up into its question-level links: one HP link per distinct
 * attribute path, one AP link per distinct (appliance, operator, attributeKey).
 *
 * A question's stateMapper is the set of profile targets it touches, and for a question
 * WITH choices that set is exactly what its choices' clauses address. The BE owns the
 * derivation on save — it serializes these links into stateMapper for AUTO rows (SM-9 /
 * SM-11) — so the client's job is to send the links, never the string. Feeding the result
 * to serializeStateMapper reproduces the same value locally for live preview (SM-20).
 */
export function deriveQuestionLinks(choices: ClauseBearing[] | undefined): {
  homeProfileLinks: HomeProfileQuestionLink[]
  applianceLinks: ApplianceQuestionLink[]
} {
  const homeProfileLinks: HomeProfileQuestionLink[] = []
  const applianceLinks: ApplianceQuestionLink[] = []
  const seenHp = new Set<string>()
  const seenAp = new Set<string>()

  for (const choice of choices ?? []) {
    for (const c of choice.hpMapperClauses ?? []) {
      // Mid-edit clauses can still be blank; they are not a target yet.
      if (!c.attributePath || seenHp.has(c.attributePath)) continue
      seenHp.add(c.attributePath)
      homeProfileLinks.push({ attributePath: c.attributePath })
    }
    for (const c of choice.apMapperClauses ?? []) {
      // One clause can address several appliances (&/| groups, fan-outs) — each target
      // is its own link, and the same appliance under two operators is two links.
      for (const applianceId of c.applianceIds ?? []) {
        const key = `${applianceId}:${c.operator}:${c.attributeKey ?? ''}`
        if (seenAp.has(key)) continue
        seenAp.add(key)
        applianceLinks.push({
          applianceId,
          operator: c.operator,
          ...(c.attributeKey ? { attributeKey: c.attributeKey } : {}),
        })
      }
    }
  }

  return { homeProfileLinks, applianceLinks }
}

function serializeHpClause(c: HpMapperClause): string {
  const path = c.attributePath
  if (c.role === 'READ') {
    if (c.operator === 'C') return `HP:${path}:C:${serializeRange(c)}`
    return `HP:${path}:D:${c.value ?? ''}`
  }
  return `HP:${path}:${c.isNull ? NULL : c.value ?? ''}`
}

function serializeApTarget(c: ApMapperClause): string {
  const ids = c.applianceIds ?? []
  // No appliance chosen yet mid-edit (SM-21+ live preview) — never a real save input,
  // where there's always at least one id, but worth not rendering the literal string
  // "undefined" while the builder cascade is still in progress.
  if (ids.length === 0) return ''
  if (ids.length === 1) return String(ids[0])
  return ids.join(c.groupOperator === 'AND' ? '&' : '|')
}

function serializeApClause(c: ApMapperClause): string {
  const target = serializeApTarget(c)
  const neg = c.negate ? '!' : ''
  let value: string
  if (c.isNull) {
    value = c.operator === 'A' ? `${c.attributeKey ?? ''}-${NULL}` : NULL
  } else if (c.operator === 'C') {
    value = c.role === 'READ' ? serializeRange(c) : c.value ?? ''
  } else if (c.operator === 'A') {
    value = `${c.attributeKey ?? ''}-${neg}${c.value ?? ''}`
    if (c.secondaryKey) value += `=${c.secondaryKey}-${c.secondaryValue ?? ''}`
  } else {
    value = `${neg}${c.value ?? ''}`
  }
  return `AP:${target}:${c.operator}:${value}`
}

type ClauseBearing = Pick<AnswerChoice, 'hpMapperClauses' | 'apMapperClauses'>

function serializeRole(choice: ClauseBearing, role: MapperRole): string | null {
  const parts: string[] = []
  for (const c of choice.hpMapperClauses ?? []) if (c.role === role) parts.push(serializeHpClause(c))
  for (const c of choice.apMapperClauses ?? []) if (c.role === role) parts.push(serializeApClause(c))
  return parts.length ? parts.join(',') : null
}

export interface SerializedChoiceMappers {
  readMapper: string | null
  writeMapper: string | null
  unselectMapper: string | null
}

export function serializeChoiceMappers(choice: ClauseBearing): SerializedChoiceMappers {
  return {
    readMapper: serializeRole(choice, 'READ'),
    writeMapper: serializeRole(choice, 'WRITE'),
    unselectMapper: serializeRole(choice, 'UNSELECT'),
  }
}
